/**
 * Derive a terse, human-readable message from a dashboard-widget data-fetch
 * failure, for display in the widget error box and inline next to the live
 * "Updated …" freshness label.
 *
 * - A response carrying a backend-provided `message` uses that message.
 * - A response without a usable message uses `responded with <status>`
 *   (e.g. "responded with 502").
 * - A request that never received a response (connection refused, DNS, CORS,
 *   offline) is a "Network error".
 * - An aborted / timed-out request is "Request timed out".
 */
export const describeWidgetFetchError = (err: any, fallback = "Failed to load."): string => {
    const response = err?.response;
    if (response) {
        const backendMessage = response.data?.message;
        if (typeof backendMessage === "string" && backendMessage.trim()) {
            return backendMessage.trim();
        }
        if (response.status) {
            return `responded with ${response.status}`;
        }
        return fallback;
    }
    if (err?.code === "ECONNABORTED" || /timeout/i.test(err?.message ?? "")) {
        return "Request timed out";
    }
    if (err?.code === "ERR_NETWORK" || err?.message === "Network Error") {
        return "Network error";
    }
    return typeof err?.message === "string" && err.message ? err.message : fallback;
};
